import "./user-team-cultural-fit.css";

// Short Report: User vs Team - Cultural Fit
// Unique classnames prefixed with tt-utcf- to avoid conflicts
const UserTeamCulturalFitReport = (comparisonResult) => {
  console.log("UserTeamCulturalFitReport comparisonResult:", comparisonResult);
  const sr = comparisonResult?.comparisonData?.userTeamCulturalFitShortReport ||
    comparisonResult?.userTeamCulturalFitShortReport || {
      team: {
        team_name: "FinOps",
        Spirit: { avgScore: 6, minScore: 5, maxScore: 7 },
        Profession: { avgScore: 8, minScore: 7, maxScore: 9 },
        Rewards: { avgScore: 5, minScore: 4, maxScore: 6 },
        Purpose: { avgScore: 7, minScore: 6, maxScore: 8 },
      },
      user: {
        user_name: "Alice Johnson",
        Spirit: 7,
        Profession: 6,
        Rewards: 8,
        Purpose: 6,
      },
      overview:
        "Culturally, Alice aligns on Spirit (7) and sits at the band edge on Purpose (6); she's below the team norm on Profession (6 vs 8) and above on Rewards (8 vs 5). Expect quick rapport and drive, but calibrate on craftsmanship/standards (raise Profession) and motivation balance (tie rewards to impact, not just incentives). A clear quality bar, peer reviews, and impact-linked recognition will smooth integration.",
    };

  const teamName = sr?.team?.team_name ?? "Team";
  const userName = sr?.user?.user_name ?? "User";

  const teamSpirit = sr?.team?.Spirit?.avgScore ?? 0;
  const teamProfession = sr?.team?.Profession?.avgScore ?? 0;
  const teamRewards = sr?.team?.Rewards?.avgScore ?? 0;
  const teamPurpose = sr?.team?.Purpose?.avgScore ?? 0;

  const userSpirit = sr?.user?.Spirit ?? 0;
  const userProfession = sr?.user?.Profession ?? 0;
  const userRewards = sr?.user?.Rewards ?? 0;
  const userPurpose = sr?.user?.Purpose ?? 0;

  const dimensions = [
    { name: "Spirit", team: teamSpirit, user: userSpirit },
    { name: "Profession", team: teamProfession, user: userProfession },
    { name: "Rewards", team: teamRewards, user: userRewards },
    { name: "Purpose", team: teamPurpose, user: userPurpose },
  ];

  // Personality labels and bucketing
  const PERSONALITY_LABELS = ["Profound", "Gifted", "Intense"];
  const personalityIndex = (v) => (Number(v) >= 8 ? 2 : Number(v) >= 5 ? 1 : 0);
  const toPersonality = (v) => PERSONALITY_LABELS[personalityIndex(v)];

  // Team personality (based on team avgScore)
  const teamPersonality = {
    Spirit: toPersonality(teamSpirit),
    Profession: toPersonality(teamProfession),
    Rewards: toPersonality(teamRewards),
    Purpose: toPersonality(teamPurpose),
  };

  // User personality (based on user score)
  const userPersonality = {
    Spirit: toPersonality(userSpirit),
    Profession: toPersonality(userProfession),
    Rewards: toPersonality(userRewards),
    Purpose: toPersonality(userPurpose),
  };

  return (
    <div className="tt-utcf-short-report-container">
      <div className="tt-utcf-report-header">
        <h3>Team Cultural Fit</h3>
        <p>Personality vectors evaluation for team dynamics harmony</p>
      </div>

      <h3>
        {
          comparisonResult.comparisonData.cultureFitAnalysisShortReport.teamData
            .name
        }
        - Avg Scores
      </h3>

      {/* Metric Cards - Team Averages */}
      <div className="tt-utcf-comparison-summary">
        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.spirit.maxScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.spirit.maxLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Spirit</div>
          </div>
        </div>

        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
              <path d="M4 22h16" />
              <path d="M10 14.66V17c0 .55.47.98.97 1.21C12.04 18.75 13 19.5 14 19.5s1.96-.75 3.03-1.29c.5-.23.97-.66.97-1.21v-2.34" />
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.profession.maxScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.profession.maxLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Profession</div>
          </div>
        </div>

        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.reward.maxScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.reward.maxLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Rewards</div>
          </div>
        </div>

        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.purpose.maxScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .teamData.teamAptitude.purpose.maxLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Purpose</div>
          </div>
        </div>
      </div>

      <h3>
        {
          comparisonResult.comparisonData.cultureFitAnalysisShortReport.userData
            .name
        }
      </h3>

      <div className="tt-utcf-comparison-summary">
        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.spiritScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.spiritScoreLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Spirit</div>
          </div>
        </div>

        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
              <path d="M4 22h16" />
              <path d="M10 14.66V17c0 .55.47.98.97 1.21C12.04 18.75 13 19.5 14 19.5s1.96-.75 3.03-1.29c.5-.23.97-.66.97-1.21v-2.34" />
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.professionScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.professionScoreLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Profession</div>
          </div>
        </div>

        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.rewardsScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.rewardsScoreLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Rewards</div>
          </div>
        </div>

        <div className="tt-utcf-summary-card">
          <div className="tt-utcf-summary-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
          </div>
          <div className="tt-utcf-summary-content">
            <div className="tt-utcf-summary-value">
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.purposeScore
              }
            </div>
            <div
              className="tt-utcf-summary-label"
              style={{ fontWeight: "600", color: "#333" }}
            >
              {
                comparisonResult.comparisonData.cultureFitAnalysisShortReport
                  .userData.aptitudeScores.purposeScoreLabel
              }
            </div>
            <div className="tt-utcf-summary-label">Purpose</div>
          </div>
        </div>
      </div>

      {/* Progress Bars Comparison */}
      {/*<div className="tt-utcf-metrics-comparison">
        <h3>Cultural Dimensions Comparison</h3>
        <div className="tt-utcf-metrics-chart">
          {dimensions.map((dim, index) => (
            <div key={index} className="tt-utcf-dimension-group">
              <div className="tt-utcf-dimension-title" style={{textAlign: "left"}}>{dim.name}</div>

              <div className="tt-utcf-metric-bar-container">
                <div className="tt-utcf-metric-info">
                  <span className="tt-utcf-metric-name">Team Avg</span>
                </div>
                <div className="tt-utcf-metric-bar">
                  <div
                    className="tt-utcf-metric-fill tt-utcf-metric-fill--team"
                    style={{ width: `${Math.max(0, Math.min(100, (dim.team / 10) * 100))}%` }}
                  ></div>
                  <span className="tt-utcf-metric-value">{dim.team}</span>
                </div>
              </div>

              <div className="tt-utcf-metric-bar-container">
                <div className="tt-utcf-metric-info">
                  <span className="tt-utcf-metric-name">User</span>
                </div>
                <div className="tt-utcf-metric-bar">
                  <div
                    className="tt-utcf-metric-fill tt-utcf-metric-fill--user"
                    style={{ width: `${Math.max(0, Math.min(100, (dim.user / 10) * 100))}%` }}
                  ></div>
                  <span className="tt-utcf-metric-value">{dim.user}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>*/}

      {/* Analysis Overview */}
      <div className="tt-utcf-insights-section">
        <h3>Analysis Overview</h3>
        <div className="tt-utcf-analysis-paragraph">
          <p>
            {
              comparisonResult.comparisonData.cultureFitAnalysisShortReport
                .overview
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserTeamCulturalFitReport;
